/**
 * 3DS 콜백 복귀 outcome 안내 문구 (#650).
 *
 * admin #1846 이 3DS 콜백 브라우저 복귀를
 * `/payment/result?source=3ds&outcome=declined|invalid` 303 redirect 로 전송한다.
 * PaymentResultPage 가 실패 화면에서 outcome 별 구분 메시지를 표시할 때 사용한다.
 *
 * - `declined`: 발급은행(카드사) 거절 — 다른 카드/잠시 후 재시도 유도
 * - `invalid`: 3DS 검증 실패(정보 불량/검증 거부) — 결제 정보 확인 후 처음부터 재시도 유도
 *
 * outcome 쿼리는 표시 힌트일 뿐이며 최종 상태는 서버 결제상태 조회(SoT)가 결정한다.
 * 15개 지원 locale 전수 정의 — i18n drift 는 paymentResult3ds.test.ts 가 회귀 게이트.
 *
 * @see src/components/payment/PaymentResultPage.tsx
 */
import type { Locale } from '@/config/locales';

export interface PaymentResult3dsTranslation {
  /** 거절(declined) 화면 제목 */
  declinedTitle: string;
  /** 거절(declined) 사유 설명 — 발급은행 거절 안내 */
  declinedDescription: string;
  /** 거절(declined) 재시도 유도 문구 — 다른 카드/잠시 후 재시도 */
  declinedRetryHint: string;
  /** 검증 실패(invalid) 화면 제목 */
  invalidTitle: string;
  /** 검증 실패(invalid) 사유 설명 — 3DS 인증 검증 실패 안내 */
  invalidDescription: string;
  /** 검증 실패(invalid) 재시도 유도 문구 */
  invalidRetryHint: string;
}

export const paymentResult3dsTranslations: Record<Locale, PaymentResult3dsTranslation> = {
  es: {
    declinedTitle: 'El banco rechazó el pago',
    declinedDescription:
      'El banco emisor de tu tarjeta rechazó el pago. No se realizó ningún cargo.',
    declinedRetryHint: 'Intenta con otra tarjeta o vuelve a intentarlo más tarde.',
    invalidTitle: 'No se pudo verificar el pago',
    invalidDescription:
      'La verificación de seguridad (3DS) del pago no fue válida. No se realizó ningún cargo.',
    invalidRetryHint: 'Revisa los datos de tu tarjeta e intenta el pago de nuevo desde el inicio.',
  },
  en: {
    declinedTitle: 'The bank declined the payment',
    declinedDescription:
      'Your card issuing bank declined the payment. No charge has been made.',
    declinedRetryHint: 'Please try a different card or try again later.',
    invalidTitle: 'Payment could not be verified',
    invalidDescription:
      'The payment security verification (3DS) was not valid. No charge has been made.',
    invalidRetryHint: 'Please check your card details and start the payment again.',
  },
  ko: {
    declinedTitle: '카드사가 결제를 거절했습니다',
    declinedDescription: '카드 발급은행이 결제를 거절했습니다. 금액은 청구되지 않았습니다.',
    declinedRetryHint: '다른 카드로 시도하시거나 잠시 후 다시 시도해 주세요.',
    invalidTitle: '결제를 검증할 수 없습니다',
    invalidDescription: '결제 보안 인증(3DS) 검증에 실패했습니다. 금액은 청구되지 않았습니다.',
    invalidRetryHint: '카드 정보를 확인하신 후 처음부터 다시 결제를 진행해 주세요.',
  },
  pt: {
    declinedTitle: 'O banco recusou o pagamento',
    declinedDescription:
      'O banco emissor do seu cartão recusou o pagamento. Nenhuma cobrança foi feita.',
    declinedRetryHint: 'Tente com outro cartão ou tente novamente mais tarde.',
    invalidTitle: 'Não foi possível verificar o pagamento',
    invalidDescription:
      'A verificação de segurança (3DS) do pagamento não foi válida. Nenhuma cobrança foi feita.',
    invalidRetryHint: 'Verifique os dados do seu cartão e refaça o pagamento desde o início.',
  },
  fr: {
    declinedTitle: 'La banque a refusé le paiement',
    declinedDescription:
      "La banque émettrice de votre carte a refusé le paiement. Aucun montant n'a été débité.",
    declinedRetryHint: 'Essayez avec une autre carte ou réessayez plus tard.',
    invalidTitle: "Impossible de vérifier le paiement",
    invalidDescription:
      "La vérification de sécurité (3DS) du paiement n'était pas valide. Aucun montant n'a été débité.",
    invalidRetryHint:
      'Vérifiez les informations de votre carte et recommencez le paiement depuis le début.',
  },
  zh: {
    declinedTitle: '银行拒绝了此次付款',
    declinedDescription: '您的发卡银行拒绝了此次付款。未产生任何扣款。',
    declinedRetryHint: '请尝试使用其他卡片，或稍后重试。',
    invalidTitle: '无法验证付款',
    invalidDescription: '付款安全验证（3DS）未通过。未产生任何扣款。',
    invalidRetryHint: '请确认卡片信息后，从头重新进行付款。',
  },
  ja: {
    declinedTitle: 'カード発行会社が決済を拒否しました',
    declinedDescription: 'カード発行銀行が決済を拒否しました。請求は発生していません。',
    declinedRetryHint: '別のカードでお試しいただくか、しばらくしてから再度お試しください。',
    invalidTitle: '決済を検証できませんでした',
    invalidDescription: '決済のセキュリティ認証（3DS）の検証に失敗しました。請求は発生していません。',
    invalidRetryHint: 'カード情報をご確認のうえ、最初から決済をやり直してください。',
  },
  hi: {
    declinedTitle: 'बैंक ने भुगतान अस्वीकार कर दिया',
    declinedDescription:
      'आपके कार्ड जारीकर्ता बैंक ने भुगतान अस्वीकार कर दिया। कोई राशि नहीं काटी गई है।',
    declinedRetryHint: 'कृपया किसी अन्य कार्ड से प्रयास करें या बाद में पुनः प्रयास करें।',
    invalidTitle: 'भुगतान सत्यापित नहीं हो सका',
    invalidDescription:
      'भुगतान की सुरक्षा जांच (3DS) मान्य नहीं थी। कोई राशि नहीं काटी गई है।',
    invalidRetryHint: 'कृपया कार्ड विवरण जांचें और भुगतान शुरुआत से दोबारा करें।',
  },
  de: {
    declinedTitle: 'Die Bank hat die Zahlung abgelehnt',
    declinedDescription:
      'Die ausstellende Bank Ihrer Karte hat die Zahlung abgelehnt. Es wurde nichts abgebucht.',
    declinedRetryHint:
      'Versuchen Sie es mit einer anderen Karte oder später erneut.',
    invalidTitle: 'Zahlung konnte nicht verifiziert werden',
    invalidDescription:
      'Die Sicherheitsprüfung (3DS) der Zahlung war ungültig. Es wurde nichts abgebucht.',
    invalidRetryHint:
      'Überprüfen Sie Ihre Kartendaten und starten Sie die Zahlung erneut von vorne.',
  },
  it: {
    declinedTitle: 'La banca ha rifiutato il pagamento',
    declinedDescription:
      'La banca emittente della tua carta ha rifiutato il pagamento. Nessun addebito è stato effettuato.',
    declinedRetryHint: "Prova con un'altra carta o riprova più tardi.",
    invalidTitle: 'Impossibile verificare il pagamento',
    invalidDescription:
      'La verifica di sicurezza (3DS) del pagamento non era valida. Nessun addebito è stato effettuato.',
    invalidRetryHint: "Controlla i dati della carta e ripeti il pagamento dall'inizio.",
  },
  ru: {
    declinedTitle: 'Банк отклонил платёж',
    declinedDescription:
      'Банк, выпустивший вашу карту, отклонил платёж. Средства не были списаны.',
    declinedRetryHint: 'Попробуйте другую карту или повторите попытку позже.',
    invalidTitle: 'Не удалось проверить платёж',
    invalidDescription:
      'Проверка безопасности платежа (3DS) не пройдена. Средства не были списаны.',
    invalidRetryHint: 'Проверьте данные карты и начните оплату заново.',
  },
  ar: {
    declinedTitle: 'رفض البنك عملية الدفع',
    declinedDescription: 'رفض البنك المُصدر لبطاقتك عملية الدفع. لم يتم خصم أي مبلغ.',
    declinedRetryHint: 'يرجى المحاولة ببطاقة أخرى أو إعادة المحاولة لاحقًا.',
    invalidTitle: 'تعذّر التحقق من عملية الدفع',
    invalidDescription: 'فشل التحقق الأمني (3DS) لعملية الدفع. لم يتم خصم أي مبلغ.',
    invalidRetryHint: 'يرجى التحقق من بيانات البطاقة وبدء عملية الدفع من جديد.',
  },
  vi: {
    declinedTitle: 'Ngân hàng đã từ chối thanh toán',
    declinedDescription:
      'Ngân hàng phát hành thẻ của bạn đã từ chối thanh toán. Chưa có khoản tiền nào bị trừ.',
    declinedRetryHint: 'Vui lòng thử thẻ khác hoặc thử lại sau.',
    invalidTitle: 'Không thể xác minh thanh toán',
    invalidDescription:
      'Xác minh bảo mật (3DS) của giao dịch không hợp lệ. Chưa có khoản tiền nào bị trừ.',
    invalidRetryHint: 'Vui lòng kiểm tra thông tin thẻ và thực hiện lại thanh toán từ đầu.',
  },
  th: {
    declinedTitle: 'ธนาคารปฏิเสธการชำระเงิน',
    declinedDescription:
      'ธนาคารผู้ออกบัตรของคุณปฏิเสธการชำระเงิน ยังไม่มีการเรียกเก็บเงินใด ๆ',
    declinedRetryHint: 'กรุณาลองใช้บัตรใบอื่นหรือลองใหม่อีกครั้งภายหลัง',
    invalidTitle: 'ไม่สามารถยืนยันการชำระเงินได้',
    invalidDescription:
      'การตรวจสอบความปลอดภัย (3DS) ของการชำระเงินไม่ถูกต้อง ยังไม่มีการเรียกเก็บเงินใด ๆ',
    invalidRetryHint: 'กรุณาตรวจสอบข้อมูลบัตรแล้วเริ่มชำระเงินใหม่ตั้งแต่ต้น',
  },
  nl: {
    declinedTitle: 'De bank heeft de betaling geweigerd',
    declinedDescription:
      'De uitgevende bank van je kaart heeft de betaling geweigerd. Er is niets afgeschreven.',
    declinedRetryHint: 'Probeer een andere kaart of probeer het later opnieuw.',
    invalidTitle: 'Betaling kon niet worden geverifieerd',
    invalidDescription:
      'De beveiligingsverificatie (3DS) van de betaling was ongeldig. Er is niets afgeschreven.',
    invalidRetryHint: 'Controleer je kaartgegevens en start de betaling opnieuw.',
  },
};
